import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiArrowUpRight, FiArrowDownLeft, FiShoppingBag, FiLayers, FiGift, FiTrash2, FiClock, FiRefreshCcw, FiExternalLink } = FiIcons;

const CreditHistory = ({ userId, refreshKey }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentsMap, setPaymentsMap] = useState({});

  const fetchLogs = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('app_credit_logs')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLogs(data || []);

      // If there are purchase logs, try to fetch the corresponding payment records
      const purchaseLogs = data?.filter(l => l.type === 'purchase') || [];
      if (purchaseLogs.length > 0) {
        // Extract IDs from descriptions like "Créditos liberados via Webhook Asaas (ID: pay_xxx)"
        const paymentIds = purchaseLogs.map(l => {
          const match = l.description.match(/ID: (pay_[a-z0-9]+)/);
          return match ? match[1] : null;
        }).filter(Boolean);

        if (paymentIds.length > 0) {
          const { data: payments } = await supabase
            .from('app_payments_v2')
            .select('asaas_payment_id, billing_type, payload')
            .in('asaas_payment_id', paymentIds);
          
          const map = {};
          payments?.forEach(p => {
            map[p.asaas_payment_id] = p;
          });
          setPaymentsMap(map);
        }
      }
    } catch (err) {
      console.error('Error fetching credit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs, refreshKey]);

  const getIcon = (type) => {
    switch (type) {
      case 'purchase': return FiShoppingBag;
      case 'usage': return FiLayers;
      case 'donation': return FiGift;
      case 'removal': return FiTrash2;
      default: return FiClock;
    }
  };

  const getBadgeColor = (type, amount) => {
    if (amount > 0) return 'bg-green-100 text-green-700';
    return 'bg-red-100 text-red-700';
  };

  const formatType = (type) => {
    const types = {
      purchase: 'Compra',
      usage: 'Uso em Teste',
      donation: 'Bônus/Ajuste',
      removal: 'Remoção'
    };
    return types[type] || type;
  };

  const renderDescription = (log) => {
    if (log.type === 'purchase') {
      const match = log.description.match(/ID: (pay_[a-z0-9]+)/);
      const paymentId = match ? match[1] : null;
      const payment = paymentsMap[paymentId];
      
      if (payment) {
        const method = payment.billing_type === 'UNDEFINED' ? 'Asaas' : payment.billing_type;
        return (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-bold uppercase tracking-tight">
              Pagamento via {method}
            </span>
            {payment.payload?.invoiceUrl && (
              <a 
                href={payment.payload.invoiceUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors"
                title="Ver Fatura"
              >
                <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
              </a>
            )}
          </div>
        );
      }
      return <span className="text-xs text-slate-500 font-bold uppercase tracking-tight">Pagamento Realizado</span>;
    }
    return <span className="text-xs text-slate-500 font-bold uppercase tracking-tight">{log.description}</span>;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-lg font-black text-slate-800 uppercase italic leading-none">Extrato de Créditos</h3>
          <p className="text-[10px] text-slate-400 font-bold uppercase mt-1 tracking-widest">Histórico de movimentações</p>
        </div>
        <button 
          onClick={fetchLogs}
          className="p-3 bg-white border border-slate-100 rounded-2xl text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all shadow-sm"
          title="Atualizar"
        >
          <SafeIcon icon={FiRefreshCcw} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="bg-white rounded-[32px] border border-slate-100 overflow-hidden shadow-sm">
        {loading && logs.length === 0 ? (
          <div className="p-20 text-center">
            <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Carregando seu histórico...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-20 text-center">
            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
              <SafeIcon icon={FiClock} className="text-2xl" />
            </div>
            <h4 className="text-slate-900 font-black uppercase italic text-sm">Nenhuma movimentação</h4>
            <p className="text-slate-400 text-[10px] font-bold uppercase mt-1 tracking-widest">Suas transações aparecerão aqui.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[600px]">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Data e Hora</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Tipo</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Descrição</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {logs.map((log) => (
                  <motion.tr 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    key={log.id} 
                    className="hover:bg-slate-50/30 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-slate-700">
                          {new Date(log.created_at).toLocaleDateString()}
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium">
                          {new Date(log.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-lg ${log.amount > 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                          <SafeIcon icon={getIcon(log.type)} className="text-xs" />
                        </div>
                        <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-tighter ${getBadgeColor(log.type, log.amount)}`}>
                          {formatType(log.type)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {renderDescription(log)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex flex-col items-end">
                        <span className={`text-sm font-black flex items-center gap-1 ${log.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          <SafeIcon icon={log.amount > 0 ? FiArrowUpRight : FiArrowDownLeft} />
                          {log.amount > 0 ? '+' : ''}{log.amount}
                        </span>
                        {log.brl_value > 0 && (
                          <span className="text-[10px] text-slate-400 font-black uppercase tracking-tighter">
                            R$ {log.brl_value.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditHistory;